import React, { useCallback, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import type { IntegrationDto, Job } from '../../types';
import apiClient from '../../shared/api/apiClient';

interface CreateJobModalProps {
    integration: IntegrationDto | null;
    isOpen: boolean;
    onClose: () => void;
    onJobCreated: (newJob: Job) => void;
}

type ScheduleType = 'daily' | 'weekly' | 'monthly' | 'hourly' | 'custom';

interface ScheduleConfig {
    type: ScheduleType;
    hour: number;
    minute: number;
    daysOfWeek: number[]; // 0-6, Sunday = 0
    daysOfMonth: number[]; // 1-31
    interval: number; // for hourly schedules
}

const CreateJobModal: React.FC<CreateJobModalProps> = ({
    integration,
    isOpen,
    onClose,
    onJobCreated,
}) => {
    const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
        type: 'daily',
        hour: 0,
        minute: 0,
        daysOfWeek: [1], // Monday by default
        daysOfMonth: [1], // 1st of month by default
        interval: 1
    });
    const [newCron, setNewCron] = useState<string>('');
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [cronError, setCronError] = useState<string>('');
    const [showAiCronModal, setShowAiCronModal] = useState<boolean>(false);
    const [aiDescription, setAiDescription] = useState<string>('');
    const [isGeneratingCron, setIsGeneratingCron] = useState<boolean>(false);
    
    const handleClose = useCallback(() => {
        setNewCron('');
        setCronError('');
        onClose();
    }, [onClose]);

    const validateCron = (cron: string): string => {
        const trimmed = cron.trim();
        if (!trimmed) return 'Cron выражение обязательно для заполнения';

        // Простая валидация: должно содержать 5-6 частей, разделенных пробелами
        const parts = trimmed.split(/\s+/);
        if (parts.length < 5 || parts.length > 6) {
            return 'Неверный формат Cron выражения. Должно быть 5 или 6 частей.';
        }

        return '';
    };

    const handleCreateJob = async () => {
        const error = validateCron(newCron);
        if (error) {
            setCronError(error);
            return;
        }

        if (!integration) return;

        try {
            setIsCreating(true);

            // Get user's time zone
            const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // Prepare headers with X-Time-Zone
            const headers: Record<string, string> = {
                'X-Time-Zone': userTimeZone
            };
            
            const result = await apiClient.post<Job>(`/api/Jobs/${integration.id}`, {
                    integrationId: integration.id,
                    cron: newCron.trim()
                }, headers);

            onJobCreated(result.data);

            toast.success('Задача создана успешно!');

            // Сбрасываем форму и закрываем модал
            setNewCron('');
            setCronError('');
            onClose();
        } catch (error) {
            console.error('Ошибка при создания задачи:', error);
            toast.error('Ошибка при создания задачи: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        } finally {
            setIsCreating(false);
        }
    };

    const handleGenerateCron = async () => {
        if (!aiDescription.trim()) {
            toast.error('Пожалуйста, введите описание расписания');
            return;
        }

        try {
            setIsGeneratingCron(true);
            const response = await fetch('/api/Jobs/generate-cron', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    description: aiDescription.trim()
                })
            });

            if (!response.ok) {
                throw new Error('Failed to generate cron');
            }

            const result = await response.json();
            setNewCron(result.data.cron);
            setCronError(''); // Clear any existing errors
            setShowAiCronModal(false);
            setAiDescription('');

            toast.success('CRON выражение сгенерировано!');
        } catch (error) {
            console.error('Ошибка при генерации CRON:', error);
            toast.error('Ошибка при генерации CRON: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        } finally {
            setIsGeneratingCron(false);
        }
    };

    const handleCloseAiModal = useCallback(() => {
        setShowAiCronModal(false);
        setAiDescription('');
    }, []);

    // Convert schedule config to cron expression
    const generateCronFromConfig = (config: ScheduleConfig): string => {
        const { type, hour, minute, daysOfWeek, daysOfMonth, interval } = config;

        switch (type) {
            case 'daily':
                return `${minute} ${hour} * * *`;
            case 'weekly':
                return `${minute} ${hour} * * ${daysOfWeek.join(',')}`;
            case 'monthly':
                return `${minute} ${hour} ${daysOfMonth.join(',')} * *`;
            case 'hourly':
                return `${minute} */${interval} * * *`;
            case 'custom':
            default:
                return `${minute} ${hour} * * *`;
        }
    };



    // Handle schedule type change
    const handleScheduleTypeChange = (type: ScheduleType) => {
        const newConfig = { ...scheduleConfig, type };
        setScheduleConfig(newConfig);
        // Auto-update cron after a short delay to avoid too frequent updates
        setTimeout(() => {
            const cron = generateCronFromConfig(newConfig);
            setNewCron(cron);
            setCronError('');
        }, 100);
    };

    // Handle time change
    const handleTimeChange = (hour: number, minute: number) => {
        const newConfig = { ...scheduleConfig, hour, minute };
        setScheduleConfig(newConfig);
        setTimeout(() => {
            const cron = generateCronFromConfig(newConfig);
            setNewCron(cron);
            setCronError('');
        }, 100);
    };

    // Handle day selection for weekly
    const handleDayOfWeekChange = (day: number, checked: boolean) => {
        const newDaysOfWeek = checked
            ? [...scheduleConfig.daysOfWeek, day].sort()
            : scheduleConfig.daysOfWeek.filter(d => d !== day);

        if (newDaysOfWeek.length === 0) return; // At least one day required

        const newConfig = { ...scheduleConfig, daysOfWeek: newDaysOfWeek };
        setScheduleConfig(newConfig);
        setTimeout(() => {
            const cron = generateCronFromConfig(newConfig);
            setNewCron(cron);
            setCronError('');
        }, 100);
    };

    // Handle day selection for monthly
    const handleDayOfMonthChange = (day: number, checked: boolean) => {
        const newDaysOfMonth = checked
            ? [...scheduleConfig.daysOfMonth, day].sort((a, b) => a - b)
            : scheduleConfig.daysOfMonth.filter(d => d !== day);

        if (newDaysOfMonth.length === 0) return; // At least one day required

        const newConfig = { ...scheduleConfig, daysOfMonth: newDaysOfMonth };
        setScheduleConfig(newConfig);
        setTimeout(() => {
            const cron = generateCronFromConfig(newConfig);
            setNewCron(cron);
            setCronError('');
        }, 100);
    };

    // Handle interval change for hourly
    const handleIntervalChange = (interval: number) => {
        const newConfig = { ...scheduleConfig, interval };
        setScheduleConfig(newConfig);
        setTimeout(() => {
            const cron = generateCronFromConfig(newConfig);
            setNewCron(cron);
            setCronError('');
        }, 100);
    };

    // Initialize cron expression when component mounts or schedule config changes
    useEffect(() => {
        const cron = generateCronFromConfig(scheduleConfig);
        setNewCron(cron);
        setCronError('');
    }, [scheduleConfig]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (showAiCronModal) {
                    handleCloseAiModal();
                } else {
                    handleClose();
                }
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [handleClose, handleCloseAiModal, isOpen, showAiCronModal]);

    if (!isOpen || !integration) {
        return null;
    }

    return (
        <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="user-avatar mr-3">
                                ➕
                            </div>
                            <div>
                                <h3 className="modal-title-custom">Создание новой задачи</h3>
                                <p className="modal-subtitle-custom">Интеграция "{integration.name}"</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close-custom"
                            onClick={handleClose}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <div className="modal-body-custom">
                        <form onSubmit={(e) => { e.preventDefault(); handleCreateJob(); }}>
                            <div className="form-grid">
                                {/* Schedule Type Selection */}
                                <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                    <label className="form-label-custom">
                                        <span className="form-icon">📅</span>
                                        Тип расписания
                                    </label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {[
                                            { value: 'daily', label: 'Ежедневно', icon: '📅' },
                                            { value: 'weekly', label: 'Еженедельно', icon: '📊' },
                                            { value: 'monthly', label: 'Ежемесячно', icon: '📆' },
                                            { value: 'hourly', label: 'Каждый час', icon: '⏰' },
                                            { value: 'custom', label: 'Другое', icon: '⚙️' }
                                        ].map(({ value, label, icon }) => (
                                            <button
                                                key={value}
                                                type="button"
                                                className={`btn ${scheduleConfig.type === value ? 'btn-primary' : 'btn-outline-secondary'} btn-sm`}
                                                onClick={() => handleScheduleTypeChange(value as ScheduleType)}
                                                disabled={isCreating}
                                            >
                                                <span className="mr-1">{icon}</span>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Weekly Days Selection */}
                                {scheduleConfig.type === 'weekly' && (
                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="form-icon">📊</span>
                                            Дни недели
                                        </label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {[
                                                { value: 1, label: 'Пн' },
                                                { value: 2, label: 'Вт' },
                                                { value: 3, label: 'Ср' },
                                                { value: 4, label: 'Чт' },
                                                { value: 5, label: 'Пт' },
                                                { value: 6, label: 'Сб' },
                                                { value: 0, label: 'Вс' }
                                            ].map(({ value, label }) => (
                                                <label key={value} className="form-check-custom">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={scheduleConfig.daysOfWeek.includes(value)}
                                                        onChange={(e) => handleDayOfWeekChange(value, e.target.checked)}
                                                        disabled={isCreating}
                                                    />
                                                    <span className="form-check-label">{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Monthly Days Selection */}
                                {scheduleConfig.type === 'monthly' && (
                                    <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                        <label className="form-label-custom">
                                            <span className="form-icon">📆</span>
                                            Дни месяца
                                        </label>
                                        <div className="d-flex flex-wrap gap-1">
                                            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                                <label key={day} className="form-check-custom">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={scheduleConfig.daysOfMonth.includes(day)}
                                                        onChange={(e) => handleDayOfMonthChange(day, e.target.checked)}
                                                        disabled={isCreating}
                                                    />
                                                    <span className="form-check-label">{day}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Time Selection */}
                                <div className="form-group-custom">
                                    <label className="form-label-custom">
                                        <span className="form-icon">🕐</span>
                                        {scheduleConfig.type === 'hourly' ? 'Минуты' : 'Время выполнения'}
                                    </label>
                                    <div className="d-flex gap-2 align-items-center">
                                        {scheduleConfig.type !== 'hourly' && (
                                            <>
                                                <select
                                                    className="form-select-custom"
                                                    value={scheduleConfig.hour}
                                                    onChange={(e) => handleTimeChange(parseInt(e.target.value), scheduleConfig.minute)}
                                                    disabled={isCreating}
                                                    style={{ width: 'auto', minWidth: '80px' }}
                                                >
                                                    {Array.from({ length: 24 }, (_, i) => (
                                                        <option key={i} value={i}>
                                                            {i}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span>:</span>
                                            </>
                                        )}
                                        <select
                                            className="form-select-custom"
                                            value={scheduleConfig.minute}
                                            onChange={(e) => handleTimeChange(scheduleConfig.hour, parseInt(e.target.value))}
                                            disabled={isCreating}
                                            style={{ width: 'auto', minWidth: '80px' }}
                                        >
                                            {Array.from({ length: 60 }, (_, i) => (
                                                <option key={i} value={i}>
                                                    {i.toString().padStart(2, '0')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-help">
                                        Время указывается в вашем локальном часовом поясе ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                                    </div>
                                </div>

                                {/* Hourly Interval */}
                                {scheduleConfig.type === 'hourly' && (
                                    <div className="form-group-custom">
                                        <label className="form-label-custom">
                                            <span className="form-icon">⏰</span>
                                            Интервал (часы)
                                        </label>
                                        <select
                                            className="form-select-custom"
                                            value={scheduleConfig.interval}
                                            onChange={(e) => handleIntervalChange(parseInt(e.target.value))}
                                            disabled={isCreating}
                                            style={{ width: 'auto', minWidth: '100px' }}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map((interval) => (
                                                <option key={interval} value={interval}>
                                                    Каждые {interval} час{interval > 1 ? 'а' : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Cron Expression Input - Always visible and editable */}
                                <div className="form-group-custom" style={{ gridColumn: '1 / -1' }}>
                                    <label htmlFor="cron-input" className="form-label-custom">
                                        <span className="form-icon">🔧</span>
                                        Cron выражение
                                    </label>
                                    <div className="d-flex gap-2 align-items-center">
                                        <input
                                            id="cron-input"
                                            type="text"
                                            className={`form-input-custom ${cronError ? 'is-invalid' : ''}`}
                                            value={newCron}
                                            onChange={(e) => {
                                                setNewCron(e.target.value);
                                                if (cronError) setCronError('');
                                            }}
                                            placeholder="0 0 * * * (ежедневно в полночь)"
                                            disabled={isCreating}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => setShowAiCronModal(true)}
                                            title="Сгенерировать CRON с помощью ИИ"
                                            disabled={isCreating}
                                        >
                                            🤖
                                        </button>
                                    </div>
                                    {cronError && (
                                        <div className="form-error">
                                            {cronError}
                                        </div>
                                    )}
                                    <div className="form-help">
                                        'Вы можете отредактировать сгенерированное выражение или использовать его как есть'
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-primary-custom"
                            onClick={handleCreateJob}
                            disabled={isCreating}
                        >
                            {isCreating ? (
                                <>
                                    <span className="btn-spinner">⟳</span>
                                    Создание...
                                </>
                            ) : (
                                <>
                                    <span className="btn-icon">✓</span>
                                    Создать задачу
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={handleClose}
                            disabled={isCreating}
                        >
                            <span className="btn-icon">✕</span>
                            Отмена
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Cron Generation Modal */}
            {showAiCronModal && (
                <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && handleCloseAiModal()}>
                    <div className="modal-dialog-custom animate-in">
                        <div className="modal-content-custom">
                            <div className="modal-header-custom">
                                <div className="d-flex align-items-center">
                                    <div className="user-avatar mr-3">
                                        🤖
                                    </div>
                                    <div>
                                        <h3 className="modal-title-custom">Генерация CRON с помощью ИИ</h3>
                                        <p className="modal-subtitle-custom">Опишите расписание на естественном языке</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close-custom"
                                    onClick={handleCloseAiModal}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body-custom">
                                <form onSubmit={(e) => { e.preventDefault(); handleGenerateCron(); }}>
                                    <div className="form-group-custom">
                                        <label htmlFor="ai-description-input" className="form-label-custom">
                                            <span className="form-icon">💬</span>
                                            Описание расписания
                                        </label>
                                        <textarea
                                            id="ai-description-input"
                                            className="form-textarea-custom"
                                            value={aiDescription}
                                            onChange={(e) => setAiDescription(e.target.value)}
                                            placeholder="Например: 'каждый день в 9 утра', 'каждые 2 часа', 'по понедельникам в 10:00'..."
                                            rows={4}
                                            disabled={isGeneratingCron}
                                            required
                                        />
                                        <div className="form-help">
                                            <strong>Примеры:</strong><br />
                                            • каждый день в 3 часа ночи<br />
                                            • каждые 6 часов<br />
                                            • по рабочим дням в 8:30<br />
                                            • каждую неделю по субботам в полдень<br />
                                            • каждый час с 9 до 17
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="modal-footer-custom">
                                <button
                                    type="button"
                                    className="btn-primary-custom"
                                    onClick={handleGenerateCron}
                                    disabled={isGeneratingCron || !aiDescription.trim()}
                                >
                                    {isGeneratingCron ? (
                                        <>
                                            <span className="btn-spinner">⟳</span>
                                            Генерация...
                                        </>
                                    ) : (
                                        <>
                                            <span className="btn-icon">✨</span>
                                            Сгенерировать CRON
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary-custom"
                                    onClick={handleCloseAiModal}
                                    disabled={isGeneratingCron}
                                >
                                    <span className="btn-icon">✕</span>
                                    Отмена
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateJobModal;
